// The header, the menu by role, the footer, and the banners: "paused" while the budget
// stop is on (reliability-design, The Budget Stop), and "connection down"
// when GET /me gets no answer (Step 26).
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useApi } from './api-context';
import { useCaller } from './caller-context';
import { LoadError } from './load-state';
import { useIsRegistrationsOn } from './registrations-switch';

export function AppShell(props: { children: ReactNode }) {
  const api = useApi();
  const { me, setMe, isUnreachable, refresh } = useCaller();
  const isRegistrationsOn = useIsRegistrationsOn();
  const navigate = useNavigate();
  const [isPaused, setIsPaused] = useState(false);
  const [isSignOutFailed, setIsSignOutFailed] = useState(false);

  useEffect(() => api.onPaused(() => setIsPaused(true)), [api]);

  async function signOut() {
    setIsSignOutFailed(false);
    try {
      await api.post('/api/v1/me/sign-out');
    } catch {
      // Still signed in on the server, so stay signed in here too.
      setIsSignOutFailed(true);
      return;
    }
    setMe(null);
    await navigate('/');
  }

  return (
    <>
      <header className="shell-header">
        <Link to="/" className="shell-brand" data-testid="shell-home">
          Caravan
        </Link>
        <nav aria-label="Main">
          {me?.coachStatus === 'approved' && (
            <Link to="/coach" data-testid="shell-dashboard">
              Dashboard
            </Link>
          )}
          {/* U6: the learner's own registrations, while the switch is on. */}
          {me && !me.isSignUpPending && isRegistrationsOn && (
            <Link to="/registrations" data-testid="shell-my-registrations">
              My registrations
            </Link>
          )}
          {me && !me.isSignUpPending && (
            <>
              <Link to="/account" data-testid="shell-account">
                Account
              </Link>
              <Link to="/account/privacy" data-testid="shell-privacy">
                Privacy
              </Link>
            </>
          )}
          {me && coachMenuPath(me.coachStatus) && (
            <Link to={coachMenuPath(me.coachStatus) ?? '/'} data-testid="shell-profile">
              Profile
            </Link>
          )}
          {/* U4: any coach may see their Zoom connection and disconnect. */}
          {(me?.coachStatus === 'approved' || me?.coachStatus === 'suspended') && (
            <Link to="/coach/meeting-settings" data-testid="shell-meeting-settings">
              Meeting settings
            </Link>
          )}
          {me?.isAdmin && (
            <>
              <Link to="/admin/privacy-notice" data-testid="shell-admin-notice">
                Privacy notice
              </Link>
              <Link to="/admin/coaches" data-testid="shell-admin-coaches">
                Coaches
              </Link>
              <Link to="/admin/coach-terms" data-testid="shell-admin-coach-terms">
                Coach terms
              </Link>
            </>
          )}
          {me && (
            <button
              type="button"
              className="link-button"
              data-testid="shell-sign-out"
              onClick={() => void signOut()}
            >
              Sign out
            </button>
          )}
        </nav>
      </header>
      {isPaused && (
        <p role="status" className="banner" data-testid="paused-banner">
          Caravan is paused for now. Please try again later.
        </p>
      )}
      {isUnreachable && (
        <div className="banner">
          <LoadError
            testId="connection"
            message="Could not reach Caravan. Check your connection and try again."
            onRetry={() => void refresh()}
          />
        </div>
      )}
      {isSignOutFailed && (
        <p role="alert" className="banner" data-testid="sign-out-error">
          Could not sign you out. Please try again.
        </p>
      )}
      <main className="shell-main">{props.children}</main>
      {/* U3 (A9, FR2.1): every page leads to hosting. */}
      <footer className="shell-footer">
        <Link to="/host" data-testid="shell-host">
          Host a webinar
        </Link>
      </footer>
    </>
  );
}

// U3 (A9): "Profile" for any coach; the application while not yet approved.
function coachMenuPath(coachStatus: string): string | null {
  if (coachStatus === 'approved' || coachStatus === 'suspended') return '/coach/profile';
  if (coachStatus === 'pending' || coachStatus === 'rejected') return '/coach/application';
  return null;
}
